import styled, { keyframes } from 'styled-components';

const shine = keyframes`
  0% { left: -100%; }
  50%, 100% { left: 100%; }
`;

export const Button = styled.button`
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.lg}`};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-weight: 700;
  font-size: ${({ theme }) => theme.typography.body.regular};
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  cursor: pointer;
  font-family: 'Space Grotesk', sans-serif;
  letter-spacing: 0.02em;
  position: relative;
  overflow: hidden;

  ${({ theme, variant = 'primary' }) => {
    switch (variant) {
      case 'primary':
        return `
          background: ${theme.colors.gradient};
          color: #ffffff;
          border: none;
          box-shadow: ${theme.shadows.glow};
          &:hover {
            transform: translateY(-2px);
            box-shadow: ${theme.shadows.glowLg};
            filter: brightness(1.08);
          }
          &:active {
            transform: translateY(0);
          }
          &::after {
            content: '';
            position: absolute;
            top: 0;
            height: 100%;
            width: 30px;
            background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
            animation: ${shine} 4s infinite ease-in-out;
          }
        `;
      case 'secondary':
        return `
          background-color: ${theme.colors.surfaceAlt};
          color: ${theme.colors.textDark};
          border: 1px solid ${theme.colors.border};
          &:hover {
            background-color: ${theme.colors.borderLight};
            border-color: ${theme.colors.primary}30;
            transform: translateY(-2px);
          }
        `;
      case 'outline':
        return `
          background-color: transparent;
          color: ${theme.colors.textDark};
          border: 1.5px solid ${theme.colors.border};
          &:hover {
            border-color: ${theme.colors.primary};
            color: ${theme.colors.primary};
            background: ${theme.colors.primary}08;
            transform: translateY(-2px);
            box-shadow: ${theme.shadows.glowSm};
          }
        `;
      case 'glass':
        return `
          background: ${theme.colors.glass};
          color: ${theme.colors.textDark};
          border: 1px solid ${theme.colors.glassStroke};
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          &:hover {
            background: ${theme.colors.surfaceAlt};
            border-color: ${theme.colors.primary}30;
            transform: translateY(-2px);
            box-shadow: ${theme.shadows.glowSm};
          }
        `;
      case 'text':
        return `
          background-color: transparent;
          color: ${theme.colors.textDark};
          padding: ${theme.spacing.xs} ${theme.spacing.sm};
          &:hover {
            color: ${theme.colors.primary};
          }
        `;
      default:
        return '';
    }
  }}
`;

export const Section = styled.section`
  background-color: ${({ theme, bg = 'light' }) => {
    switch (bg) {
      case 'light': return theme.colors.background;
      case 'dark': return theme.colors.dark;
      case 'gray': return theme.colors.surface;
      default: return theme.colors.background;
    }
  }};
  color: ${({ theme }) => theme.colors.textDark};
  padding: ${({ theme }) => `${theme.spacing.xxl} 0`};
  position: relative;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: ${({ theme }) => `${theme.spacing.xl} 0`};
  }
`;

export const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.heading.h2};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  position: relative;
  display: inline-block;
  font-weight: 800;
  letter-spacing: -0.03em;
  color: ${({ theme }) => theme.colors.textDark};

  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 40px;
    height: 3px;
    background: ${({ theme }) => theme.colors.gradient};
    border-radius: 2px;
  }
`;

export const SectionTitleCentered = styled(SectionTitle)`
  display: block;
  text-align: center;
  width: fit-content;
  margin: 0 auto ${({ theme }) => theme.spacing.xl};

  &::after {
    left: 50%;
    transform: translateX(-50%);
  }
`;

export const Flex = styled.div`
  display: flex;
  flex-direction: ${({ direction = 'row' }) => direction};
  justify-content: ${({ justify = 'flex-start' }) => justify};
  align-items: ${({ align = 'stretch' }) => align};
  gap: ${({ gap = '0' }) => gap};
  flex-wrap: ${({ wrap = 'nowrap' }) => wrap};
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: ${({ columns = '1fr' }) => columns};
  gap: ${({ gap = '1.5rem' }) => gap};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

export const Card = styled.div`
  background-color: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  box-shadow: ${({ theme }) => theme.shadows.card};
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  border: 1px solid ${({ theme }) => theme.colors.border};
  position: relative;

  &:hover {
    transform: translateY(-5px);
    box-shadow: ${({ theme }) => theme.shadows.cardHover};
    border-color: ${({ theme }) => theme.colors.primary}25;
  }
`;

export const Container = styled.div`
  width: 100%;
  max-width: ${({ theme }) => theme.container.maxWidth};
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.container.padding};
`;

export const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: ${({ theme, color }) => color ? `${color}10` : `${theme.colors.primary}10`};
  color: ${({ theme, color }) => color || theme.colors.primary};
  border: 1px solid ${({ theme, color }) => color ? `${color}20` : `${theme.colors.primary}20`};
  transition: all 0.3s ease;

  &:hover {
    background: ${({ theme, color }) => color ? `${color}18` : `${theme.colors.primary}18`};
    border-color: ${({ theme, color }) => color ? `${color}30` : `${theme.colors.primary}30`};
  }
`;